/**
 * usePingID — pollea cada 60s el endpoint de ARX y devuelve las cifras agregadas.
 *
 * Solo se monta cuando hay user (Firebase auth). Si no hay user, devuelve estado vacío.
 * ARX decide via allowlist server-side si el response trae cifras reales o decoy;
 * el cliente no distingue, solo muestra lo que llega.
 *
 * Stale: si la última respuesta tiene > 120s, marca `stale=true` y la UI agrega "?".
 * Persistencia: el último valor se cachea en sessionStorage para sobrevivir refresh.
 */

import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'

const ENDPOINT = 'https://arx.summafx.com/pingid/today'
const POLL_INTERVAL_MS = 60_000
const STALE_THRESHOLD_MS = 120_000
const STORAGE_KEY = 'pingid:last'

interface PingIDResponse {
  buys: string
  sells: string
  updated_at: string
}

interface PingIDState {
  buys: string | null
  sells: string | null
  stale: boolean
  lastFetch: number | null
}

function readCache(): PingIDState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return { buys: null, sells: null, stale: false, lastFetch: null }
    const parsed = JSON.parse(raw) as { buys: string; sells: string; lastFetch: number }
    return {
      buys: parsed.buys,
      sells: parsed.sells,
      lastFetch: parsed.lastFetch,
      stale: Date.now() - parsed.lastFetch > STALE_THRESHOLD_MS,
    }
  } catch {
    return { buys: null, sells: null, stale: false, lastFetch: null }
  }
}

function writeCache(buys: string, sells: string, lastFetch: number): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ buys, sells, lastFetch }))
  } catch {
    // sessionStorage puede fallar en modo privado — no es crítico
  }
}

export function usePingID(): PingIDState {
  const { user } = useAuth()
  const [state, setState] = useState<PingIDState>(readCache)
  const inFlight = useRef(false)

  useEffect(() => {
    if (!user) return

    let cancelled = false

    async function fetchOnce() {
      if (inFlight.current || !user) return
      inFlight.current = true
      try {
        const token = await user.getIdToken()
        const res = await fetch(ENDPOINT, {
          headers: { Authorization: `Bearer ${token}` },
          signal: AbortSignal.timeout(8_000),
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const body = (await res.json()) as PingIDResponse
        if (cancelled) return
        const now = Date.now()
        writeCache(body.buys, body.sells, now)
        setState({ buys: body.buys, sells: body.sells, stale: false, lastFetch: now })
      } catch {
        if (cancelled) return
        // Mantenemos el último valor; markStaleTick lo marcará si pasan 2 min
      } finally {
        inFlight.current = false
      }
    }

    // Tick que revisa si el último fetch quedó stale
    function markStaleTick() {
      if (cancelled) return
      setState(prev => {
        if (prev.lastFetch === null) return prev
        const isStale = Date.now() - prev.lastFetch > STALE_THRESHOLD_MS
        return prev.stale === isStale ? prev : { ...prev, stale: isStale }
      })
    }

    fetchOnce()
    const pollId = setInterval(fetchOnce, POLL_INTERVAL_MS)
    const staleId = setInterval(markStaleTick, 5_000)

    return () => {
      cancelled = true
      clearInterval(pollId)
      clearInterval(staleId)
    }
  }, [user])

  return state
}
